import React, { useMemo, useState, memo } from "react";
import { VerticalImage } from "@talentbait/remotion-templates-talentbait";
import { FlexContainer } from "../../AdAssistant.styled";
import { useAdAssistantDimensions } from "../../hooks/useAdAssistantDimensions";
import { TEMPLATES } from "../../AdAssistant";
import OrientationsTemplatePreview from "../../previews/orientations-template-preview/OrientationsTemplatePreview";
import { useUpdateCombinationData } from "../../hooks/useUpdateCombinationData";
import AdAssistantStore from "../../AdAssistantStore";
import { withRefluxStore } from "../../../../Stores";
import { withRouter } from "react-router-dom";
import {
  hasParamsAlready,
  removeCloudinaryParams
} from "../../../SelfServiceCampaignCreation/Previews/media-assets-lab/layout/Layout";
import { EditionPreviewWrapper } from "./EditionPreview.styled";

// Updated transformation array with objects containing `needsSizes` and `param`
const transformations = [
  // AUTO
  { needsSizes: false, needsAR: true, param: "c_auto" },
  { needsSizes: true, param: "c_auto" },
  { needsSizes: true, param: "c_auto,g_auto" },
  { needsSizes: false, param: "c_auto,g_auto" },
  // CROP
  { needsSizes: true, param: "c_crop,g_auto" },
  { needsSizes: true, param: "c_crop" },
  { needsSizes: false, param: "c_crop,g_face,z_0.6" },
  // FILL
  { needsSizes: true, needsAR: false, param: "c_fill,g_auto" }
];

// Function to replace Cloudinary URL params
const replaceCloudinaryParams = (url, newParams) => {
  // Replace or insert the new params into the URL
  const updatedUrl = url.replace(/(upload\/)(?:[^/]*\/)?(v\d+)/, (match, p1, p2) => {
    return `${p1}${newParams}/${p2}`;
  });

  return updatedUrl;
};

// Process the image URL with transformations
const processAutocroppedImageWithTransformations = ({
  url,
  orientation,
  dimensions,
  templateId = "",
  transformation
}) => {
  if (!url || !dimensions || !transformation) return url;

  try {
    const { width, cloudAr, height, widthImage, heightImage } = dimensions || {};

    if (!width || !height) {
      console.error("Missing dimensions for template:", templateId, orientation);
    }

    // Default dimensions if missing
    const finalWidth = widthImage || width || 1200;
    const finalHeight = heightImage || height || 628;

    const cleanUrl = hasParamsAlready.test(url) ? removeCloudinaryParams(url) : url;

    // If the transformation needs sizes, add the width and height parameters
    let transformedParam = transformation.param;
    if (transformation.needsSizes) {
      transformedParam = `w_${finalWidth},h_${finalHeight},${transformedParam}`;
    }
    if (transformation.needsAR) {
      transformedParam = `ar_${finalWidth}:${finalHeight},${transformedParam}`;
    }

    // Now apply the transformation
    const finalURL = replaceCloudinaryParams(cleanUrl, transformedParam);

    return finalURL;
  } catch (error) {
    return url;
  }
};

const EditionPreview = ({ editedAd, orientationSelected, userProfile, allAdCombinations }) => {
  const dimensions = useAdAssistantDimensions();
  const defaultWidth = 261;
  const defaultHeight = 436;
  const { mediaAssets: mediaAssetsAd } = editedAd;
  const defaultMediaAssetAd = mediaAssetsAd["vertical"];

  const [currentTransformationIndex, setCurrentTransformationIndex] = useState(0);

  const TemplateComponent = useMemo(
    () => TEMPLATES[defaultMediaAssetAd.templateId] || VerticalImage,
    [defaultMediaAssetAd]
  );

  const isTemplateVideo = defaultMediaAssetAd.type === "video";
  const templateId = defaultMediaAssetAd.templateId || "";

  const isSingleOrientation = orientationSelected?.value !== "all";
  const singleOrientationDimensions = dimensions[templateId][orientationSelected?.value];

  let singleOrientationAsset = "";
  if (isSingleOrientation) {
    singleOrientationAsset = isTemplateVideo
      ? mediaAssetsAd[orientationSelected?.value]?.inputProps?.video
      : mediaAssetsAd[orientationSelected?.value]?.inputProps?.backgroundImage;
  }

  const { updateCombinationData } = useUpdateCombinationData({
    currentAd: editedAd,
    userProfile,
    dimensions,
    allAdCombinations
  });

  const handleDoubleClick = (orientation) => {
    const currentCroppedImage = mediaAssetsAd[orientation]?.inputProps?.backgroundImage;

    // Get the current transformation based on the index
    const transformation = transformations[currentTransformationIndex];
    const transformedImageUrl = processAutocroppedImageWithTransformations({
      url: currentCroppedImage,
      orientation: orientationSelected?.value,
      dimensions: dimensions[templateId][orientation],
      templateId,
      transformation
    });

    // Update the transformation index for the next click, wrapping back to 0 when at the end
    setCurrentTransformationIndex((prevIndex) => (prevIndex + 1) % transformations.length);

    // You can use transformedImageUrl as needed
    updateCombinationData({ newAsset: transformedImageUrl, orientation });
  };

  const renderPreviews = () => {
    const orientationOrder = ["vertical", "portrait", "landscape"];

    if (orientationSelected?.value === "all") {
      return orientationOrder.map((orientation) => {
        const inputProps = mediaAssetsAd[orientation]?.inputProps;
        if (!inputProps) return null;

        const assetUrl = isTemplateVideo ? inputProps.video : inputProps.backgroundImage;
        const videoDuration = inputProps.duration;
        const mediaAssetDimensions = dimensions[templateId][orientation];

        return (
          <div
            key={orientation}
            style={{ justifyContent: "flex-start", marginBottom: "16px", cursor: "pointer" }}
            width={mediaAssetDimensions.width || defaultWidth}
            height={mediaAssetDimensions.height || defaultHeight}
            onDoubleClick={() => handleDoubleClick(orientation)}
          >
            <OrientationsTemplatePreview
              isTemplateVideo={isTemplateVideo}
              dimensionsPreview={mediaAssetDimensions}
              templateInputProps={inputProps}
              autoCroppingImage={assetUrl}
              orientationSelected={orientation}
              videoDuration={videoDuration}
              TemplateComponent={TemplateComponent}
              editedAd={editedAd}
              userProfile={userProfile}
            />
          </div>
        );
      });
    }

    const orientationSelectedProps = mediaAssetsAd[orientationSelected?.value]?.inputProps;

    // Single orientation preview
    return (
      <div
        style={{ justifyContent: "flex-start" }}
        width={singleOrientationDimensions?.width || defaultWidth}
        height={singleOrientationDimensions?.height || defaultHeight}
      >
        <OrientationsTemplatePreview
          isTemplateVideo={isTemplateVideo}
          dimensionsPreview={singleOrientationDimensions}
          templateInputProps={orientationSelectedProps}
          autoCroppingImage={singleOrientationAsset}
          orientationSelected={orientationSelected?.value}
          videoDuration={orientationSelectedProps?.duration}
          TemplateComponent={TemplateComponent}
          editedAd={editedAd}
          userProfile={userProfile}
        />
      </div>
    );
  };

  return (
    <EditionPreviewWrapper minHeight={"436px"} justify={"flex-start"} align={"flex-start"}>
      {renderPreviews()}
    </EditionPreviewWrapper>
  );
};

export default memo(
  withRouter(
    withRefluxStore(EditionPreview, [AdAssistantStore], ["userProfile", "allAdCombinations"])
  )
);
